import { Button, Divider, Flex, IconFont, Toast } from '@byecode/ui'
import { DragEndEvent } from '@dnd-kit/core'
import type { DTFile, SizeConfigure } from '@lighthouse/core'
import { DevicePreviewEnum } from '@lighthouse/core'
import { arrayMove, nanoid } from '@lighthouse/tools'
import type { DrawerProps } from '@mantine/core'
import { Drawer } from '@mantine/core'
import { useAbortItem } from '@rpldy/shared-ui'
import { FILE_STATES } from '@rpldy/uploady'
import equal from 'fast-deep-equal'
import { clone, find, findIndex } from 'rambda'
import React, { ReactNode, useCallback, useId, useMemo, useRef, useState } from 'react'
import { useSetState, useUpdateEffect } from 'react-use'
import { useImmer } from 'use-immer'

import type { UseUploadFileSParameter } from '../../..'
import {
    errorCodeMap,
    FILE_ICON_COLOR_MAP,
    FILE_ICON_MAP,
    getImageFullUrlInApplication,
    SortableList,
    UploadDropZone,
    useApplicationContext,
    useLanguageContext,
    useUploadBatchError,
    useUploadBatchFinished,
    useUploadBatchUploading
} from '../../..'
import type { EnableActionsParams, WithStatusFile } from '../../../types'
import { getFileNameByUrl, getFileSizeByUrl, getFileTypeByUrl } from '../../../utils/helper'
import { FilePreviewer } from '../../FilePreviewer'
import { useUploadBatchProgress } from '../../UploadManage/hooks/useUploadBatchProgress'
import FileListItem from '../FileListPreviewer/FileListItem'
import FileAdd from '../mobile/FileAdd'
import * as SC from './styles'

export const getFileDrawerConfig: (isClip?: boolean) => Pick<DrawerProps, 'zIndex' | 'styles' | 'position'> = isClip => ({
    zIndex: 200,
    styles: {
        root: { position: isClip ? 'absolute' : 'fixed' },
        header: {
            display: 'none'
        },
        drawer: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            overflow: 'hidden',
            height: 'auto'
        },
        body: {
            backgroundColor: 'var(--color-gray-50)'
        }
    },
    position: 'bottom'
})

interface State {
    openedAdd: boolean
    opened: boolean
}

export interface TriggerNodeFnProps {
    onPreview: () => void
    onRemove: (index: number) => void
}

export interface FileGridConfig {
    gap?: SizeConfigure
    borderRadius?: number
    itemWidth?: SizeConfigure
    maxCount?: number
}

interface FileUploaderProps {
    children?: React.ReactNode
    enableItemActions?: EnableActionsParams
    files: DTFile[] | undefined
    disableUpload?: boolean
    sortable?: boolean
    triggerNodeFn?: (innerFiles: WithStatusFile[], disabled: boolean | undefined, handlers: TriggerNodeFnProps) => ReactNode
    limit?: number
    previewType: DevicePreviewEnum
    uploadyOptions: Pick<UseUploadFileSParameter, 'info' | 'options'>
    uploadFileType?: 'file' | 'video'
    accept?: string
    hiddenFileList?: boolean
    gridConfig?: FileGridConfig
    onChange?: (files: DTFile[]) => void
}

export const WithoutPopperFileUploader: React.FC<FileUploaderProps> = ({
    files,
    enableItemActions,
    disableUpload,
    sortable,
    triggerNodeFn,
    previewType,
    uploadFileType,
    uploadyOptions,
    limit,
    accept,
    hiddenFileList,
    gridConfig,
    onChange
}) => {
    const { appId, pageTarget, isMobile } = useApplicationContext()
    const { convertTextByLanguage: t } = useLanguageContext()
    const [defaultIndex, setDefaultIndex] = useState(0)
    const uploadDropId = useId()
    const abortItem = useAbortItem()
    const [{ opened, openedAdd }, setState] = useSetState<State>({
        openedAdd: false,
        opened: false
    })

    const newUploadyOptions = useMemo(
        () => ({ ...uploadyOptions, info: { ...uploadyOptions.info, id: uploadDropId } }),
        [uploadDropId, uploadyOptions]
    )

    const listData: WithStatusFile[] = useMemo(() => {
        return (files ?? []).map(url => {
            const name = getFileNameByUrl(url) || ''
            const type = getFileTypeByUrl(url)
            const size = getFileSizeByUrl(url)
            return {
                uid: nanoid(),
                label: name,
                value: url,
                type,
                url: getImageFullUrlInApplication(appId, url),
                name: name ?? '',
                status: 'success',
                percent: 100,
                size: Number(size)
            }
        })
    }, [appId, files])
    const [innerFiles, setInnerFiles] = useImmer(listData)
    const getBatchBizKey = useCallback((batchItem: any) => {
        const file = batchItem?.file
        const name = file?.name || ''
        const type = getFileTypeByUrl(name)
        const batchId = batchItem?.batchId || 'no-batch'
        return `${batchId}:sig:${name}:${Number(file?.size ?? 0)}:${type}:${Number(file?.lastModified ?? 0)}`
    }, [])
    const getUploadFileKey = useCallback((file: Pick<WithStatusFile, 'name' | 'size' | 'type'>) => {
        return (file as any).__bizKey || `${file.name}:${Number(file.size ?? 0)}:${file.type}`
    }, [])
    const normalizeDraftFiles = useCallback(
        (draft: WithStatusFile[]) => {
            const seenUploadingKey = new Set<string>()
            const next: WithStatusFile[] = []

            draft.forEach(item => {
                if (item.status === 'uploading') {
                    const key = getUploadFileKey(item)
                    if (seenUploadingKey.has(key)) {
                        return
                    }
                    seenUploadingKey.add(key)
                    next.push(item)
                    return
                }

                next.push(item)
            })

            draft.splice(0, draft.length, ...next)
        },
        [getUploadFileKey]
    )

    const disabled = (limit && limit <= innerFiles.length) || disableUpload
    const maxFileCount = limit != null ? Math.max(0, limit - innerFiles.length) : undefined
    const oldFileRef = useRef(listData)

    useUpdateEffect(() => {
        if (!equal(oldFileRef.current, listData)) {
            oldFileRef.current = listData
            setInnerFiles(listData)
        }
    }, [listData])

    useUploadBatchUploading(uploadDropId, (batchItems, batchId) => {
        const currentBatchItems = batchItems.filter(item => item.batchId === batchId)
        const uniqueBatchItems = [...currentBatchItems].reduce((acc, item) => {
            const { file, state, completed, loaded } = item
            const key = getBatchBizKey(item)
            const prev = acc.get(key)
            if (!prev) {
                acc.set(key, item)
                return acc
            }

            const prevIsUploading = prev.state === FILE_STATES.UPLOADING
            const curIsUploading = state === FILE_STATES.UPLOADING
            const prevCompleted = prev.completed ?? 0
            const curCompleted = completed ?? 0
            const prevLoaded = prev.loaded ?? 0
            const curLoaded = loaded ?? 0
            if ((curIsUploading && !prevIsUploading) || curCompleted > prevCompleted || curLoaded > prevLoaded) {
                acc.set(key, item)
            }
            return acc
        }, new Map<string, any>())

        const items: WithStatusFile[] = [...uniqueBatchItems.values()].map(item => {
            const { file, id, completed } = item
            const { name, size } = file
            const fileType = getFileTypeByUrl(name)
            const percentNum = Number(completed ?? 0)
            return {
                uid: id,
                name,
                label: name,
                value: '',
                type: fileType,
                status: 'uploading',
                url: '',
                size,
                percent: Number.isFinite(percentNum) ? percentNum : 0,
                __bizKey: getBatchBizKey(item)
            }
        })

        let didAbortDueToLimit = false
        setInnerFiles(draft => {
            items.forEach(item => {
                const incomingKey = getUploadFileKey(item)
                const index = findIndex(file => file.uid === item.uid, draft)
                if (index >= 0) {
                    draft[index] = item
                    return
                }
                const sameUploadingIndexes = draft.reduce<number[]>((acc, file, idx) => {
                    if (file.status === 'uploading' && getUploadFileKey(file) === incomingKey) {
                        acc.push(idx)
                    }
                    return acc
                }, [])
                if (sameUploadingIndexes.length > 0) {
                    const [keepIndex, ...removeIndexes] = sameUploadingIndexes
                    draft[keepIndex] = item
                    removeIndexes
                        .sort((a, b) => b - a)
                        .forEach(i => {
                            draft.splice(i, 1)
                        })
                    return
                }
                if (limit && draft.length >= limit) {
                    didAbortDueToLimit = true
                    abortItem(item.uid ?? '')
                    return
                }
                draft.push(item)
            })
            normalizeDraftFiles(draft)
        })
        if (didAbortDueToLimit) {
            Toast.error(t('uploadFailedMaxFiles', { count: limit }))
        }
    })

    useUploadBatchError(uploadDropId, batchItems => {
        setInnerFiles(draft => {
            draft.forEach(item => {
                const { uid } = item
                const uploadingFileIndex = findIndex(({ id }) => uid === id, batchItems)
                if (uploadingFileIndex !== -1) {
                    const uploadFile = draft[uploadingFileIndex]
                    if (uploadFile) {
                        uploadFile.value = ''
                        uploadFile.status = 'error'
                        uploadFile.percent = 100
                    }
                }
            })
            normalizeDraftFiles(draft)
        })
    })

    useUploadBatchProgress(uploadDropId, batchItems => {
        const validProgressItems = batchItems.filter(item => {
            const completed = Number(item.completed ?? 0)
            return item.state === FILE_STATES.UPLOADING || item.state === FILE_STATES.PENDING || completed > 0
        })
        if (validProgressItems.length === 0) {
            return
        }

        const uniqueBatchItems = [...validProgressItems].reduce((acc, item) => {
            const { file, state, completed, loaded } = item
            const key = getBatchBizKey(item)
            const prev = acc.get(key)
            if (!prev) {
                acc.set(key, item)
                return acc
            }
            const prevIsUploading = prev.state === FILE_STATES.UPLOADING
            const curIsUploading = state === FILE_STATES.UPLOADING
            const prevCompleted = prev.completed ?? 0
            const curCompleted = completed ?? 0
            const prevLoaded = prev.loaded ?? 0
            const curLoaded = loaded ?? 0
            if ((curIsUploading && !prevIsUploading) || curCompleted > prevCompleted || curLoaded > prevLoaded) {
                acc.set(key, item)
            }
            return acc
        }, new Map<string, any>())

        const mergedBatchItems = [...uniqueBatchItems.values()]
        const batchItemMap = [...uniqueBatchItems.entries()].reduce((acc, [key, item]) => {
            acc.set(key, item)
            return acc
        }, new Map<string, any>())
        const idToBizKey = mergedBatchItems.reduce((acc, item) => {
            acc.set(item.id, getBatchBizKey(item))
            return acc
        }, new Map<string, string>())
        setInnerFiles(draft => {
            draft.forEach(item => {
                const uploadingFile = batchItemMap.get(getUploadFileKey(item)) || find(({ id }) => item.uid === id, mergedBatchItems)
                if (uploadingFile) {
                    item.status = 'uploading'
                    item.percent = uploadingFile.completed
                    if (item.uid !== uploadingFile.id) {
                        item.uid = uploadingFile.id
                    }
                    ;(item as any).__bizKey = idToBizKey.get(uploadingFile.id) || getUploadFileKey(item)
                }
            })
            normalizeDraftFiles(draft)
        })
    })

    useUploadBatchFinished(uploadDropId, batchItems => {
        setInnerFiles(draft => {
            batchItems.forEach(batch => {
                const { uploadResponse, id } = batch
                const finishedKey = getBatchBizKey(batch)

                const sameUploadingIndexes = draft.reduce<number[]>((acc, item, idx) => {
                    if (item.status === 'uploading' && getUploadFileKey(item) === finishedKey) {
                        acc.push(idx)
                    }
                    return acc
                }, [])

                const idIndex = findIndex(({ uid }) => uid === id, draft)
                const keepIndex = idIndex >= 0 ? idIndex : sameUploadingIndexes[0]
                if (keepIndex === undefined || keepIndex < 0) {
                    return
                }

                const uploadFile = draft[keepIndex]
                const httpCode = uploadResponse.data?.code

                if (uploadResponse.data?.code === '0000000') {
                    const url = uploadResponse.data.content ? getImageFullUrlInApplication(appId, uploadResponse.data.content.url) : ''
                    uploadFile.percent = 100
                    uploadFile.value = uploadResponse.data.content.url || ''
                    uploadFile.url = url
                    uploadFile.status = uploadResponse.data.content ? 'success' : 'error'
                    ;(uploadFile as any).__bizKey = finishedKey
                } else {
                    Toast.error(errorCodeMap[httpCode as keyof typeof errorCodeMap] || '未知错误')
                    uploadFile.value = ''
                    uploadFile.status = 'error'
                    uploadFile.percent = 100
                    ;(uploadFile as any).__bizKey = finishedKey
                }

                sameUploadingIndexes
                    .filter(idx => idx !== keepIndex)
                    .sort((a, b) => b - a)
                    .forEach(i => {
                        draft.splice(i, 1)
                    })
            })
            normalizeDraftFiles(draft)
            onChange?.(draft.map(v => v.value).filter(Boolean))
        })
        setState({ opened: false, openedAdd: false })
    })

    const handleFileRemove = useCallback(
        (index: number) => {
            setInnerFiles(draft => {
                draft.splice(index, 1)
                onChange?.(clone(draft).map(item => item.value))
            })
        },
        [onChange, setInnerFiles]
    )

    const handlePreview = useCallback(
        (index = 0) => {
            setDefaultIndex(index)
            setState({ opened: true })
        },
        [setState]
    )

    const handleFileSort = useCallback(
        (ev: DragEndEvent) => {
            const {
                active: { id: activeId },
                over
            } = ev
            const overId = over?.id
            if (!overId) {
                return
            }

            const sourceIndex = findIndex(file => file.uid === activeId, innerFiles)
            const targetIndex = findIndex(file => file.uid === overId, innerFiles)

            const sortedFiles = arrayMove(innerFiles, sourceIndex, targetIndex)

            setInnerFiles(sortedFiles)

            onChange?.(sortedFiles.map(file => file.value).filter(Boolean))
        },
        [innerFiles, onChange, setInnerFiles]
    )

    const renderFileListItem = useCallback(
        (item: WithStatusFile, index: number) => {
            return (
                <FileListItem
                    key={item.uid}
                    data={item}
                    disabled={disabled}
                    // card 主要用于字段 block 中的文件列表变体
                    variant="card"
                    previewType={previewType}
                    enableActions={enableItemActions}
                    onPreview={() => {
                        handlePreview(index)
                    }}
                    onRemove={() => {
                        handleFileRemove(index)
                    }}
                />
            )
        },
        [disabled, enableItemActions, handleFileRemove, handlePreview, previewType]
    )

    const filesContent = useMemo(() => {
        if (hiddenFileList || !innerFiles || innerFiles.length === 0) {
            return
        }

        if (gridConfig) {
            const resolvedItemWidth = gridConfig.itemWidth ? `${gridConfig.itemWidth.size}${gridConfig.itemWidth.unit || 'px'}` : '80px'
            const itemBorderRadius = gridConfig.borderRadius != null ? `${gridConfig.borderRadius}px` : '8px'

            const displayedFiles = gridConfig.maxCount != null ? innerFiles.slice(0, gridConfig.maxCount) : innerFiles
            const overflowCount = gridConfig.maxCount != null ? Math.max(0, innerFiles.length - gridConfig.maxCount) : 0

            return (
                <>
                    {displayedFiles.map((item, index) => {
                        const { uid, type, url, name } = item
                        const isImage = type === 'image'
                        const iconType = FILE_ICON_MAP[type] || FILE_ICON_MAP.other
                        const iconColor = FILE_ICON_COLOR_MAP[type] || FILE_ICON_COLOR_MAP.other
                        const isLastAndHasOverflow = overflowCount > 0 && index === displayedFiles.length - 1
                        return (
                            <div
                                key={uid}
                                style={{
                                    position: 'relative',
                                    width: resolvedItemWidth,
                                    height: resolvedItemWidth,
                                    flexShrink: 0,
                                    borderRadius: itemBorderRadius,
                                    overflow: 'hidden',
                                    background: 'var(--color-gray-100)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    gap: 4
                                }}
                                onClick={() => handlePreview(index)}
                            >
                                {isImage && url ? (
                                    <img
                                        src={getImageFullUrlInApplication(appId, url)}
                                        alt={name}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                    />
                                ) : (
                                    <>
                                        <IconFont type={iconType} color={iconColor} size={28} />
                                        <div
                                            style={{
                                                fontSize: 11,
                                                color: 'var(--color-gray-500)',
                                                textAlign: 'center',
                                                padding: '0 4px',
                                                overflow: 'hidden',
                                                whiteSpace: 'nowrap',
                                                textOverflow: 'ellipsis',
                                                maxWidth: '100%'
                                            }}
                                        >
                                            {name}
                                        </div>
                                    </>
                                )}
                                {isLastAndHasOverflow && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            inset: 0,
                                            background: 'rgba(0, 0, 0, 0.45)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#fff',
                                            fontSize: 18,
                                            fontWeight: 500
                                        }}
                                    >
                                        +{overflowCount}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </>
            )
        }

        return (
            <SortableList
                rowKey="uid"
                sortable={sortable}
                onSortEnd={handleFileSort}
                data={innerFiles}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    marginTop: '8px'
                }}
                itemStyle={{ padding: '6px 0' }}
                itemRender={(item, index) => renderFileListItem(item, index)}
            />
        )
    }, [appId, gridConfig, handleFileSort, handlePreview, hiddenFileList, innerFiles, renderFileListItem, sortable])

    const fileUploaderContent = useMemo(() => {
        if (disabled && !hiddenFileList) {
            return null
        }
        return (
            <>
                {previewType === DevicePreviewEnum.DESKTOP ? (
                    <SC.UploadButtonWrapper>
                        <UploadDropZone
                            multiple={!limit || limit > 1}
                            maxFileCount={maxFileCount}
                            uploadOptions={newUploadyOptions}
                            disabled={disabled}
                            accept={accept}
                        >
                            {triggerNodeFn ? (
                                triggerNodeFn(innerFiles, disabled, {
                                    onPreview: () => handlePreview(),
                                    onRemove: (index: number) => handleFileRemove(index)
                                })
                            ) : (
                                <Button
                                    size="lg"
                                    icon={
                                        <Flex data-field-icon>
                                            <IconFont type="Add" size={16} />
                                        </Flex>
                                    }
                                >
                                    {t('clickToUpload')} {limit ? `(${innerFiles.length}/${limit})` : ''}
                                </Button>
                            )}
                        </UploadDropZone>
                    </SC.UploadButtonWrapper>
                ) : (
                    <SC.UploadButtonWrapper onClick={() => !disabled && setState({ openedAdd: true })}>
                        {triggerNodeFn ? (
                            triggerNodeFn(innerFiles, disabled, {
                                onPreview: () => handlePreview(),
                                onRemove: (index: number) => handleFileRemove(index)
                            })
                        ) : (
                            <Button
                                size="lg"
                                icon={
                                    <Flex data-field-icon>
                                        <IconFont type="Add" size={16} />
                                    </Flex>
                                }
                            >
                                {t('clickToUpload')}
                            </Button>
                        )}
                    </SC.UploadButtonWrapper>
                )}
            </>
        )
    }, [
        disabled,
        hiddenFileList,
        previewType,
        limit,
        maxFileCount,
        newUploadyOptions,
        accept,
        triggerNodeFn,
        innerFiles,
        t,
        setState,
        handleFileRemove,
        handlePreview
    ])

    const resolvedGridGap = gridConfig?.gap ? `${gridConfig.gap.size}${gridConfig.gap.unit || 'px'}` : '8px'

    return (
        <SC.FileUploaderWrapper
            style={
                gridConfig
                    ? {
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: resolvedGridGap,
                          alignItems: 'flex-start'
                      }
                    : undefined
            }
        >
            {fileUploaderContent}
            {filesContent}
            <FilePreviewer
                target={pageTarget}
                defaultIndex={defaultIndex}
                fileList={innerFiles}
                opened={opened}
                onClose={() => setState({ opened: false })}
            />
            {/* 移动端添加图片 */}
            <Drawer
                {...getFileDrawerConfig(Boolean(pageTarget))}
                target={pageTarget}
                opened={openedAdd}
                onClose={() => setState({ openedAdd: false })}
            >
                <FileAdd
                    uploadyOptions={newUploadyOptions}
                    uploadFileType={uploadFileType}
                    disableUpload={disabled}
                    isMobile={isMobile}
                    accept={accept}
                    multiple={!limit || limit > 1}
                    maxFileCount={maxFileCount}
                />
                <Divider style={{ borderWidth: 8, marginBottom: 52 }} color="var(--color-gray-100)" />
                <SC.Footer style={{ height: 52 }} onClick={() => setState({ openedAdd: false })}>
                    {t('cancel')}
                </SC.Footer>
            </Drawer>
        </SC.FileUploaderWrapper>
    )
}
